import {plainToInstance} from 'class-transformer';

import {NamespaceSocket, RequestQueue} from '../core/namespaceSocket';
import {ApiResponse} from '../entities/apiResponse';
import {ApiError} from '../entities/error';
import {GroupMessagesDirection} from '../entities/groups/group';
import {Message} from '../entities/messages/message';
import {MessageRequest} from '../entities/messages/messageRequest';
import {
  IMessageClientToServerEvents,
  IMessageRequestChangeResponse,
  IMessageServerToClientEvents,
} from '../interfaces/socket.interface';
import {Manager} from 'socket.io-client/build/esm/manager';

/**
 * Socket class, that works with Message namespace
 *
 * @internal
 * */
export class MessageSocket extends NamespaceSocket<MessageRequest, IMessageClientToServerEvents, IMessageServerToClientEvents> {

  /**
   * Time, for how long we will wait for new changed, before send it
   *
   * @internal
   * */
  public requestQueueTime = 300;

  /**
   * @ignore
   * @internal
   * */
  constructor(manager: Manager, success: () => void, errorCallback: (err: ApiError) => void) {
    super(manager, 'message', success, errorCallback);
  }

  /**
   * Here we will get all message changes for this group
   *
   * @param groupId to subscribe
   * @param callback that will be triggered, when event will be called, array of {@link IMessageRequestChangeResponse} will be sent
   * */
  public subscribeToChanges(groupId: string, callback: (items: IMessageRequestChangeResponse[]) => void) {
    this.socket.on(`change/${groupId}` as 'change', (data) => {
      callback(data.items);
    });
  }

  /**
   * Unsubscribe from changes
   *
   * @param groupId to unsubscribe
   * */
  public unSubscribeFromChanges(groupId: string) {
    this.socket.off(`change/${groupId}` as 'change');
  }

  /**
   * Unsubscribe from changes
   *
   * @param groupId - group id from where took messages
   * @param direction - check {@link GroupMessagesDirection} for more
   * @param limit - limit, max 100, 0 for all
   * @param offset - not used
   * @param messageId - from which messages starts
   *
   * @return Promise with array of {@link Message}
   * */
  public getMessages(groupId: string, direction: GroupMessagesDirection = null, limit = 50, offset = 0, messageId: string = null) {
    return new Promise<Message[]>((resolve, reject) => {
      this.socket.emit('get', {
        groupId, direction, limit, offset, messageId
      }, (response: ApiResponse) => {
        if (response.status) {
          // @TODO: remove when back fix
          const subI = response.result.items.map(item => {
            return {
              ...item,
              content: {
                ...item.content,
                attachments: item.content.attachments.filter(att => !!att)
              }
            }
          })
          resolve(plainToInstance(Message, subI) as unknown as Message[])
        } else {
          reject(response);
        }
      })
    });
  }

  /**
   * @ignore
   * @private
   * */
  protected findRequest(request: MessageRequest) {
    let existedRequest: RequestQueue<MessageRequest> = null;
    if (request.messageId) {
      existedRequest = this.requestQueueNew.find(item => item.request.messageId === request.messageId)
    }

    return existedRequest;
  }
}
